import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { Animated } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { SnackContent, SnackLevel } from '@src/navigation/components/SnackContent'
import { NavigatorContext } from '@src/utils/navigatorContext'
import { IconName } from '@src/components/base'
import QuickActions from '@src/config/quickActions'
import { DynamicStatusBar } from '@src/components/DynamicStatusBar'
import { LoadingIndicator } from '@src/components/LoadingIndicator'
import { SnackContentRedesigned } from '../components/SnackContentRedesigned'

const FADE_ANIMATION_DURATION = 300

export const createNavigator = (WrappedComponent: React.ComponentType<any>) => {
  const Navigator = () => {
    const styles = useStyleSheet(themedStyles)

    const opacity = useRef(new Animated.Value(0)).current

    const [snackVisible, setSnackVisible] = useState(false)
    const [snackVariant, setSnackVariant] = useState<'default' | 'redesigned'>('default')
    const [snackTitle, setSnackTitle] = useState('')
    const [snackContent, setSnackContent] = useState<string | undefined | null>(null)
    const [snackLevel, setSnackLevel] = useState<SnackLevel>('success')
    const [snackAction, setSnackAction] = useState<
      { label: string; onPress: () => void } | undefined
    >()
    const [snackIconName, setSnackIconName] = useState<IconName | undefined>(undefined)
    const [forceGlobalLoader, setForceGlobalLoader] = useState(false)

    useEffect(() => {
      if (forceGlobalLoader) {
        // Fade in animation
        Animated.timing(opacity, {
          toValue: 1,
          duration: FADE_ANIMATION_DURATION,
          useNativeDriver: true,
        }).start()
      } else {
        // Fade out animation
        Animated.timing(opacity, {
          toValue: 0,
          duration: FADE_ANIMATION_DURATION,
          useNativeDriver: true,
        }).start()
      }
    }, [forceGlobalLoader, opacity])

    const showSnack = useCallback(
      (
        snackTitle: string,
        snackContent?: string | null,
        snackLevel: SnackLevel = 'success',
        iconName?: IconName,
        action?: {
          label: string
          onPress: () => void
        },
        variant: 'default' | 'redesigned' = 'default',
      ) => {
        setSnackVisible(true)
        setSnackVariant(variant)
        setSnackIconName(iconName)
        setSnackAction(action)
        setSnackTitle(snackTitle)
        setSnackContent(snackContent)
        setSnackLevel(snackLevel)
      },
      [],
    )

    const hideSnack = useCallback(() => {
      setSnackVisible(false)
    }, [])

    const enableLoader = useCallback(() => {
      setForceGlobalLoader(true)
    }, [])

    const disableLoader = useCallback(() => {
      setForceGlobalLoader(false)
    }, [])

    useEffect(() => {
      QuickActions.configure()
    }, [])

    const screenProps = useMemo(
      () => ({
        showSnack,
        hideSnack,
        enableLoader,
        disableLoader,
      }),
      [showSnack, hideSnack, enableLoader, disableLoader],
    )

    const isRedesignedSnack = snackVariant === 'redesigned'

    return (
      <>
        <DynamicStatusBar />
        <NavigatorContext.Provider value={screenProps}>
          <WrappedComponent />
        </NavigatorContext.Provider>
        <Animated.View
          style={[styles.loaderContainer, { opacity }]}
          pointerEvents={forceGlobalLoader ? 'auto' : 'none'}
        >
          <LoadingIndicator size="large" viewStyles={styles.spinner} color="white" />
        </Animated.View>

        {isRedesignedSnack ? (
          <SnackContentRedesigned
            title={snackTitle}
            level={snackLevel}
            iconName={snackIconName}
            action={snackAction}
            isVisible={snackVisible}
            onDismiss={hideSnack}
          />
        ) : (
          <SnackContent
            title={snackTitle}
            content={snackContent}
            level={snackLevel}
            isVisible={snackVisible}
            onDismiss={hideSnack}
          />
        )}
      </>
    )
  }

  return Navigator
}

const themedStyles = StyleService.create({
  loaderContainer: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)' as 'theme.transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinner: {
    flex: 0,
  },
})
